// ⚠️ 자동 생성 파일 — 직접 수정 금지. 정본은 dentcluster-agent/shared/statement-alias.js
//    수정 절차: 정본 수정 → node scripts/sync-shared.js (release-all.sh가 불일치 시 빌드 중단)
// [공통 정본] 보철·재료 별칭 사전과 표준키 접기 — 단가표 매칭·표준명칭의 기반 사전.
// 원출처: 웹 src/utils/statementItems.js (2026-07-26 신설분)에서 분리.
//
// ★ 이 파일이 정본(shared/). 현재 사용처는 웹뿐이라 사본은 웹만 생성:
//   - 웹: src/utils/shared/statement-alias.js (ESM 변환, statementItems.js가 재수출)
//   - 에이전트 사본 없음 (에이전트가 쓰게 되면 scripts/sync-shared.js의 MODULES에서 agent:true로)
//   수정 후 반드시 `node scripts/sync-shared.js` 실행 (release-all.sh가 불일치 시 중단).
// NFC 통일(#22): normKey 앞단 normalize('NFC') — 맥 유래 NFD 한글 명칭의 별칭 매칭 어긋남 예방.

const normKey = (s) => (s || '').normalize('NFC').trim().toLowerCase();

// ── 보철·재료 별칭 사전 (2026-07-26) — CAD별 표기(exocad 한글·3shape 영문·AnatomicCrown 등)를 표준키로 접음
// 실측: 수집 의뢰서 prosthesis는 AnatomicCrown/Crown/AnatomicPontic, 단가표는 "크라운"·"지르크라운"·"Zr Crown" 등 수기
const ALIAS_MAP = {
    crown: ['크라운', '지르크라운', 'zr crown', 'zirconia crown', 'crown', 'anatomiccrown', 'anatomic crown', '지르코니아 크라운', 'full crown', 'coping', 'metal cr', 'metal crown'],
    pontic: ['폰틱', 'pontic', 'anatomicpontic', 'anatomic pontic', '브릿지', 'bridge'],
    inlay: ['인레이', '지르인레이', 'zr inlay', 'inlay', 'onlay', '온레이', '하이브리드인레이', 'hybrid inlay', 'metal inlay', '메탈 인레이'],
    veneer: ['비니어', '베니어', 'veneer', '라미네이트', 'laminate'],
    // 임플란트 종류 병합 (2026-07-30 대표 확정): 어버트먼트류 전부 → 임플란트 하나로 인식
    // 실측: exocad ImplantType=CustomAbutment(310건) / 3shape typeClass=Abutment(31건)
    implant: ['임플란트', 'implant', '어버트먼트', '커스텀어버트먼트', 'abutment', 'custom abutment',
        'customabutment', '커스텀', 'premilledabutment', 'pre milled abutment', 'tibase', '티베이스'],
    zirconia: ['지르코니아', 'zirconia', 'zirconia monolithic', 'zirconia - monolithic', 'zr', '지르'],
    pmma: ['pmma', '템포러리', 'temporary', 'temp crown', '템프', '아크릴', 'acrylic'],
    denture: ['덴쳐', '덴처', 'denture', '틀니'],
    gold: ['골드', 'gold', '골드크라운', 'gold crown'],
    wax: ['왁스', 'wax', '왁스업', 'waxup', 'wax-up'],
    // 재료 별칭 (2026-07-30 대표 확정): CAD별 영문·한글 표기를 하나로 병합
    // 실측: exocad=한글(컴퍼지트·티타늄 등) / 3shape=영문(Titanium, Chrome-Cobalt, PFM - Non-precious)
    composite: ['컴퍼지트', '컴포짓', '컴포지트', '컴퍼짓', 'composite'],
    titanium: ['티타늄', 'titanium'],
    emax: ['리튬 디실리케이트', '리튬디실리케이트', 'lithium disilicate', 'emax', 'e.max', '이맥스'],
    metal: ['메탈', 'metal', '크롬코발트', '코발트크롬', 'chrome cobalt', 'chromecobalt', 'cocr'],
    pfm: ['pfm', '도재소부'],
    hybrid: ['하이브리드', 'hybrid', 'hybrid ceramic', 'hybridceramic'],
    // 보철 별칭 추가 (2026-07-30 대표 승인): 서베이드·포스트
    surveyed: ['서베이드', 'surveyed crown', 'surveyed', '서베이드크라운'],
    post: ['포스트', 'post', '포스트코어', 'post core'],
};
const _aliasIndex = (() => {
    const m = new Map();
    for (const [canon, list] of Object.entries(ALIAS_MAP)) {
        for (const a of list) m.set(a.replace(/[\s\-_]/g, ''), canon);
    }
    return m;
})();
// 이름 문자열 안에서 재료 표준키 탐지 ("하이브리드 인레이"→hybrid) — 조합 명칭의 재료 구분용
const _MAT_CANON_SET = ['zirconia', 'composite', 'titanium', 'emax', 'metal', 'pfm', 'pmma', 'gold', 'wax', 'hybrid'];
const _matAliases = (() => {
    const out = [];
    for (const canon of _MAT_CANON_SET) for (const a of (ALIAS_MAP[canon] || [])) out.push([a.replace(/[\s\-_]/g, ''), canon]);
    return out.sort((x, y) => y[0].length - x[0].length);
})();
const detectMaterialInName = (s) => {
    const k = normKey(s).replace(/[\s\-_]/g, '');
    if (!k) return '';
    for (const [alias, canon] of _matAliases) if (k.includes(alias)) return canon;
    return '';
};

// 명칭 → 표준키 ("지르크라운"→crown, "AnatomicCrown"→crown). 미등재는 정규화 문자열 그대로
const canonKey = (s) => {
    const k = normKey(s).replace(/[\s\-_]/g, '');
    if (!k) return '';
    if (_aliasIndex.has(k)) return _aliasIndex.get(k);
    // 부분 포함(긴 별칭 우선): "지르코니아 크라운(대구치)" 같은 변형 흡수
    for (const [alias, canon] of [..._aliasIndex.entries()].sort((a, b) => b[0].length - a[0].length)) {
        if (k.includes(alias)) return canon;
    }
    return k;
};

export { normKey, ALIAS_MAP, canonKey, detectMaterialInName };